import type { ListBuilderConfiguration } from '../types';

/**
 * Item Client History list configuration
 * Shows client purchase history for a specific item
 * Features custom chart component for trend visualization
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_CLIENT_HISTORY_CONFIG: ListBuilderConfiguration = {
	filters: {
		activeFilters: ['clientHistoryKeywords', 'salespeople', 'itemClientTrend'],
		defaultFilters: {},
	},
	lists: [
		{
			id: 'default-client-history-all',
			cacheTTL: 600000,
			name: 'All Client History',
			type: 'default',
			active: true,
			searchEnabled: true,
			searchPlaceholder: 'Search clients...',
			// This tab shows invoice-derived purchase history, so the
			// salespeople filter is locked the same way the invoice list
			// locks invoiceSalesRep (see BASE_FILTER_RULES in
			// libs/core/.../invoice/shared.ts): users with invoices:view:any
			// filter freely; everyone else is locked to Me and the SQL is
			// further restricted by the service-level RBAC predicate to
			// primary OR alternate sales rep.
			filterRules: {
				salespeople: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'invoices:view:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'invoices:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'invoices:view:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'client',
					fields: [
						{
							id: 'client-name-field',
							link: {
								type: 'object',
								enabled: true,
								idField: 'clientId',
								objectTable: 'client',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'clientName', objectTable: 'client' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'statusInfo' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'address-line-1-field',
							dataConfig: {
								type: 'template',
								template: '{address1} {address2}',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'address-line-2-field',
							dataConfig: {
								type: 'template',
								template: '{city}, {state} {zip}',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Client' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'salesperson',
					fields: [
						{
							id: 'salesperson-name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'salespersonName' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'salesperson-header', text: 'Salesperson' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'lastPurchase',
					fields: [
						{
							id: 'last-purchase-total-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchaseTotal' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-4',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'last-purchase-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchaseQuantity' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-5',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'last-purchase-price-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchasePrice' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'last-purchase-header-1', text: 'Last Purchase $' },
						{ id: 'last-purchase-header-line-break-1', text: '' },
						{ id: 'last-purchase-header-2', text: 'Qty' },
						{ id: 'last-purchase-header-line-break-2', text: '' },
						{ id: 'last-purchase-header-3', text: 'Per' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities',
					width: 150,
					fields: [
						{
							id: 'total-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-6',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'max-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'maxQuantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-7',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'avg-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'avgQuantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 1 },
							},
							displayStyle: 'number',
						},
					],
					headers: [
						{ id: 'h4', text: '12-Month Total' },
						{ id: 'h4-line-break-1', text: '' },
						{ id: 'h5', text: '12-Month Max' },
						{ id: 'h5-line-break-2', text: '' },
						{ id: 'h6', text: '12-Month Avg' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities30',
					fields: [
						{
							id: 'total-quantity-30-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast30Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-30-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'purchases-30-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'purchasesLast30Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
					],
					headers: [
						{ id: 'h-30-1', text: '30-Day Total' },
						{ id: 'h-30-line-break-1', text: '' },
						{ id: 'h-30-2', text: '30-Day Orders' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities60',
					fields: [
						{
							id: 'total-quantity-60-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast60Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-60-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'max-quantity-60-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'maxQuantityLast60Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-60-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'avg-quantity-60-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'avgQuantityLast60Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 1 },
							},
							displayStyle: 'number',
						},
					],
					headers: [
						{ id: 'h-60-1', text: '60-Day Total' },
						{ id: 'h-60-line-break-1', text: '' },
						{ id: 'h-60-2', text: '60-Day Max' },
						{ id: 'h-60-line-break-2', text: '' },
						{ id: 'h-60-3', text: '60-Day Avg' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities90',
					fields: [
						{
							id: 'total-quantity-90-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast90Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-90-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'max-quantity-90-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'maxQuantityLast90Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-90-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'avg-quantity-90-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'avgQuantityLast90Days' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 1 },
							},
							displayStyle: 'number',
						},
					],
					headers: [
						{ id: 'h-90-1', text: '90-Day Total' },
						{ id: 'h-90-line-break-1', text: '' },
						{ id: 'h-90-2', text: '90-Day Max' },
						{ id: 'h-90-line-break-2', text: '' },
						{ id: 'h-90-3', text: '90-Day Avg' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'chart',
					fields: [
						{
							id: 'chart-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'monthlyData' },
								defaultValue: '[]',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'item-client-history-trend-chart' },
						},
					],
					headers: [{ id: 'h4', text: '12 Month Trend' }],
					minWidth: 200,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			// Includes the service-computed aggregate fields (totals, trend, and the
			// 12-month / 30 / 60 / 90-day metrics) so they are selectable in the list
			// configurator's "Select Field" dropdown rather than rendering blank.
			selectedFields: [
				{ fieldName: 'clientId', fieldLabel: 'ID' },
				{ fieldName: 'clientName', fieldLabel: 'Name' },
				{ fieldName: 'salespersonName', fieldLabel: 'Salesperson' },
				{ fieldName: 'totalQuantity', fieldLabel: 'Lifetime Quantity' },
				{ fieldName: 'totalSpent', fieldLabel: 'Lifetime Spent' },
				{ fieldName: 'avgUnitPrice', fieldLabel: 'Avg Unit Price' },
				{ fieldName: 'purchaseFrequency', fieldLabel: 'Purchase Frequency' },
				{ fieldName: 'trend', fieldLabel: 'Trend' },
				{ fieldName: 'purchasesLast12Months', fieldLabel: 'Purchases (12 Mo)' },
				{ fieldName: 'quantityLast12Months', fieldLabel: '12-Month Total' },
				{ fieldName: 'maxQuantityLast12Months', fieldLabel: '12-Month Max' },
				{ fieldName: 'avgQuantityLast12Months', fieldLabel: '12-Month Avg' },
				{ fieldName: 'monthlyData', fieldLabel: 'Monthly Trend Data' },
				{ fieldName: 'quantityLast30Days', fieldLabel: '30-Day Total' },
				{ fieldName: 'purchasesLast30Days', fieldLabel: '30-Day Orders' },
				{ fieldName: 'quantityLast60Days', fieldLabel: '60-Day Total' },
				{ fieldName: 'maxQuantityLast60Days', fieldLabel: '60-Day Max' },
				{ fieldName: 'avgQuantityLast60Days', fieldLabel: '60-Day Avg' },
				{ fieldName: 'quantityLast90Days', fieldLabel: '90-Day Total' },
				{ fieldName: 'maxQuantityLast90Days', fieldLabel: '90-Day Max' },
				{ fieldName: 'avgQuantityLast90Days', fieldLabel: '90-Day Avg' },
			],
		},
		{
			baseUrl: '/invoices/:id',
			objectLabel: 'Invoice',
			objectTable: 'invoice',
			selectedFields: [
				{ fieldName: 'lastInvoiceId', fieldLabel: 'ID' },
				{ fieldName: 'lastInvoiceExternalId', fieldLabel: 'Invoice #' },
				{ fieldName: 'lastPurchasedDate', fieldLabel: 'Last Purchase Date' },
				{ fieldName: 'lastPurchaseTotal', fieldLabel: 'Last Purchase $' },
				{ fieldName: 'lastPurchaseQuantity', fieldLabel: 'Last Purchase Qty' },
				{ fieldName: 'lastPurchasePrice', fieldLabel: 'Last Purchase Price' },
			],
		},
	],
};
